import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../lib';
import {
  type DTCAudienceQualityScoreRequest,
  type DTCQuotaInfoRequest,
  getDTCAudienceQualityScore,
  getDTCQuotaInfo,
  type PublishDTCAudienceRequest,
  type PublishDTCAudienceResponse,
  publishDTCAudience,
  type RequestDTCAudienceRequest,
  type RequestDTCAudienceResponse,
  requestDTCAudience,
  type SelectDTCAudienceRequest,
  type SelectDTCAudienceResponse,
  selectDTCAudience,
} from '../../lib/api/dtc';
import { API_TAGS } from '../../lib/constants';

export enum DTC_API_TAGS {
  requestDTCAudience = 'DTC - Request Audience',
  getDTCAudienceQualityScore = 'DTC - Get Audience Quality Score',
  getDTCAudienceQuotaInfo = 'DTC - Get Audience Quota Info',
  publishDTCAudience = 'DTC - Publish Audience',
  selectDTCAudience = 'DTC - Select Audience',
}
interface UseRequestDTCAudienceOptions {
  onSuccess?: (data: RequestDTCAudienceResponse) => void;
  onError?: (error: unknown) => void;
}

export const useRequestDTCAudience = ({ onSuccess, onError }: UseRequestDTCAudienceOptions = {}) => {
  return useMutation<RequestDTCAudienceResponse, unknown, RequestDTCAudienceRequest>({
    mutationKey: [DTC_API_TAGS.requestDTCAudience],
    mutationFn: requestDTCAudience,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [API_TAGS.SingleWorkspace],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDTCAudienceQualityScore = (
  params: DTCAudienceQualityScoreRequest,
  options?: { enabled?: boolean; staleTime?: number },
) => {
  return useQuery({
    queryKey: [DTC_API_TAGS.getDTCAudienceQualityScore, params],
    queryFn: () => getDTCAudienceQualityScore(params),
    enabled: options?.enabled ?? true,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
  });
};

export const useGetDTCAudienceQuotaInfo = (
  params: DTCQuotaInfoRequest,
  options?: { enabled?: boolean; staleTime?: number },
) => {
  return useQuery({
    queryKey: [DTC_API_TAGS.getDTCAudienceQuotaInfo, params],
    queryFn: () => getDTCQuotaInfo(params),
    enabled: options?.enabled ?? true,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
  });
};

export const usePublishDTCAudience = (options?: { onSuccess?: (data: PublishDTCAudienceResponse) => void }) => {
  return useMutation<PublishDTCAudienceResponse, unknown, PublishDTCAudienceRequest>({
    mutationKey: [DTC_API_TAGS.publishDTCAudience],
    mutationFn: publishDTCAudience,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [API_TAGS.SingleWorkspace],
      });
      options?.onSuccess?.(data);
    },
  });
};

export const useSelectDTCAudience = (options?: { onSuccess?: (data: SelectDTCAudienceResponse) => void }) => {
  return useMutation<SelectDTCAudienceResponse, unknown, SelectDTCAudienceRequest>({
    mutationKey: [DTC_API_TAGS.selectDTCAudience],
    mutationFn: selectDTCAudience,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: [API_TAGS.SingleWorkspace, API_TAGS.Workspaces] });
      options?.onSuccess?.(data);
    },
  });
};
